// src/pages/dropshipping/DropshippingProductsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, ShoppingBag, ChevronDown, Store } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useLocation } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import {
  getProviderCatalog,
  getProviders,
  assignProduct,
  updateAssignedPrice,
  unassignProduct,
  getBanks,
  getBankBranches,
  applyToProvider,
  getDropshippingSettings,
} from '../../services/api/dropshipping';
import styles from './DropshippingProductsPage.module.css';

const DropshippingProductsPage = () => {
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const location = useLocation();
  const pendingOpenProviderRef = useRef(null);
  const [activeTab, setActiveTab] = useState(() => {
    return location.state?.tab === 'merchants' ? 'merchants' : 'products';
  });

  // Products tab state
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMerchantId, setFilterMerchantId] = useState(null);
  const [filterAssigned, setFilterAssigned] = useState('all');

  // Merchants tab state
  const [merchants, setMerchants] = useState([]);

  // Modals
  const [assignModal, setAssignModal] = useState(null);
  const [priceModal, setPriceModal] = useState(null);
  const [applyModal, setApplyModal] = useState(null);
  const [assignPrice, setAssignPrice] = useState('');

  // Apply form
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [settings, setSettings] = useState(null);
  const [applyForm, setApplyForm] = useState({
    providerBusinessId: null,
    requestedShopName: '',
    requestedPaymentCycle: '',
    deliveryCharge: '',
    resellerAccountName: '',
    resellerAccountNumber: '',
    resellerBankId: null,
    resellerBankBranchId: null,
  });
  const [applyErrors, setApplyErrors] = useState({});

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterMerchantId) params.providerBusinessId = filterMerchantId;
      const data = await getProviderCatalog(params);
      setCatalogStatus(data.status);
      setProducts(data.products || []);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterMerchantId]);

  const loadMerchants = useCallback(async () => {
    try {
      const data = await getProviders();
      setMerchants(data || []);
    } catch (err) {
      showError(err.message);
    }
  }, []);

  useEffect(() => {
    if (location.state?.openApplyForProviderId) {
      pendingOpenProviderRef.current = location.state.openApplyForProviderId;
    }
    loadProducts();
    loadMerchants();
    getBanks().then(setBanks).catch(() => {});
    getDropshippingSettings().then(setSettings).catch(() => {});
  }, [loadProducts, loadMerchants]);

  // Auto-open apply modal when navigated via invite link (after merchants + settings are loaded)
  useEffect(() => {
    const targetId = pendingOpenProviderRef.current;
    if (!targetId || merchants.length === 0) return;
    const target = merchants.find((m) => m.id === targetId);
    if (target) {
      pendingOpenProviderRef.current = null;
      openApplyModal(target);
    }
  }, [merchants, settings]);

  useEffect(() => {
    if (applyForm.resellerBankId) {
      getBankBranches(applyForm.resellerBankId).then(setBranches).catch(() => setBranches([]));
    } else {
      setBranches([]);
    }
  }, [applyForm.resellerBankId]);

  // Client-side filter for assigned/unassigned
  const displayProducts = products.filter((p) => {
    if (filterAssigned === 'assigned') return p.isAssigned;
    if (filterAssigned === 'unassigned') return !p.isAssigned;
    return true;
  });

  // Connected merchants only — for the filter dropdown on the products tab
  const connectedMerchants = merchants.filter((m) => m.isConnected);

  const openAssignModal = (product) => {
    setAssignModal(product);
    setAssignPrice(product.minimumSellingPrice || product.dropshippingPrice || '');
  };

  const openApplyModal = (merchant) => {
    setApplyModal(merchant);
    const cycles = merchant.allowedPaymentCycles || [7];
    setApplyForm({
      providerBusinessId: merchant.id,
      requestedShopName: '',
      requestedPaymentCycle: cycles[0] || 7,
      deliveryCharge: '',
      resellerAccountName: settings?.resellerBankAccountName || '',
      resellerAccountNumber: settings?.resellerBankAccountNumber || '',
      resellerBankId: settings?.resellerBankId || null,
      resellerBankBranchId: settings?.resellerBankBranchId || null,
    });
    setApplyErrors({});
  };

  const handleAssign = async () => {
    if (!assignModal) return;
    try {
      await assignProduct({
        shopId: assignModal.shopId,
        productId: assignModal.id,
        sellingPrice: parseFloat(assignPrice),
      });
      success(t('dropshipping.productAssigned'));
      setAssignModal(null);
      loadProducts();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleUpdatePrice = async () => {
    if (!priceModal) return;
    try {
      await updateAssignedPrice(priceModal.productShopId, { sellingPrice: parseFloat(assignPrice) });
      success(t('dropshipping.priceUpdated'));
      setPriceModal(null);
      loadProducts();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleUnassign = async (product) => {
    confirm(t('dropshipping.confirmUnassign'), async () => {
      try {
        await unassignProduct(product.productShopId);
        success(t('dropshipping.productUnassigned'));
        loadProducts();
      } catch (err) {
        showError(err.message);
      }
    });
  };

  const validateApplyForm = () => {
    const errors = {};
    if (!applyForm.requestedShopName.trim()) errors.requestedShopName = t('dropshipping.errorShopNameRequired');
    if (applyForm.deliveryCharge === '' || applyForm.deliveryCharge === null) {
      errors.deliveryCharge = t('dropshipping.errorDeliveryChargeRequired');
    } else if (isNaN(parseFloat(applyForm.deliveryCharge)) || parseFloat(applyForm.deliveryCharge) < 0) {
      errors.deliveryCharge = t('dropshipping.errorDeliveryChargeInvalid');
    }
    if (!applyForm.resellerAccountName.trim()) errors.resellerAccountName = t('dropshipping.errorAccountNameRequired');
    if (!applyForm.resellerAccountNumber.trim()) errors.resellerAccountNumber = t('dropshipping.errorAccountNumberRequired');
    if (!applyForm.resellerBankId) errors.resellerBankId = t('dropshipping.errorBankRequired');
    if (branches.length > 0 && !applyForm.resellerBankBranchId) errors.resellerBankBranchId = t('dropshipping.errorBranchRequired');
    return errors;
  };

  const handleApply = async () => {
    const errors = validateApplyForm();
    if (Object.keys(errors).length > 0) {
      setApplyErrors(errors);
      return;
    }
    try {
      await applyToProvider({ ...applyForm, deliveryCharge: parseFloat(applyForm.deliveryCharge) });
      success(t('dropshipping.applicationSubmitted'));
      setApplyModal(null);
      loadMerchants();
    } catch (err) {
      showError(err.message);
    }
  };

  const statusLabel = (status) => {
    const map = { pending: 'pending', approved: 'approved', rejected: 'rejected' };
    return t(`dropshipping.${map[status] || 'apply'}`);
  };

  const renderProductsContent = () => {
    if (loading) {
      return <div className={styles.emptyState}>{t('dropshipping.loading')}</div>;
    }

    if (catalogStatus === 'reseller_not_enabled') {
      return (
        <div className={styles.emptyState}>
          <ShoppingBag size={40} className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>{t('dropshipping.resellerNotEnabled')}</p>
          <small>{t('dropshipping.resellerNotEnabledHelp')}</small>
        </div>
      );
    }

    if (catalogStatus === 'no_shops') {
      return (
        <div className={styles.emptyState}>
          <Store size={40} className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>{t('dropshipping.noShopsConnected')}</p>
          <small>{t('dropshipping.noShopsConnectedHelp')}</small>
          <button className={styles.btnPrimary} onClick={() => setActiveTab('merchants')} style={{ marginTop: 12 }}>
            {t('dropshipping.viewMerchants')}
          </button>
        </div>
      );
    }

    if (displayProducts.length === 0) {
      return (
        <div className={styles.emptyState}>
          <ShoppingBag size={40} className={styles.emptyIcon} />
          <p>{t('dropshipping.noProductsFound')}</p>
          <small>{t('dropshipping.noProductsDescription')}</small>
        </div>
      );
    }

    return (
      <div className={styles.productsGrid}>
        {displayProducts.map((product) => (
          <div key={product.id} className={styles.productCard}>
            {product.image ? (
              <img src={getImageUrl(product.image)} alt={product.name} className={styles.productImage} />
            ) : (
              <div className={styles.productImagePlaceholder}><ShoppingBag size={24} /></div>
            )}
            <div className={styles.productInfo}>
              {product.isAssigned && <span className={styles.assignedBadge}>{t('dropshipping.assignedBadge')}</span>}
              <div className={styles.productName}>{product.name}</div>
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>{t('dropshipping.dropshippingPrice')}</span>
                <span className={styles.priceValue}>{formatCurrency(product.dropshippingPrice)}</span>
              </div>
              {product.minimumSellingPrice && (
                <div className={styles.priceRange}>
                  {t('dropshipping.minSellingPrice')}: {formatCurrency(product.minimumSellingPrice)}
                  {' \u2014 '}
                  {t('dropshipping.maxSellingPrice')}: {formatCurrency(product.maximumSellingPrice)}
                </div>
              )}
              {product.isAssigned && product.assignedPrice && (
                <div className={styles.priceRow}>
                  <span className={styles.priceLabel}>{t('dropshipping.sellingPrice')}</span>
                  <span className={styles.priceValue}>{formatCurrency(product.assignedPrice)}</span>
                </div>
              )}
              <div className={styles.cardActions}>
                {product.isAssigned ? (
                  <>
                    <button className={styles.btnOutline} onClick={() => { setPriceModal(product); setAssignPrice(product.assignedPrice || ''); }}>
                      {t('dropshipping.updatePrice')}
                    </button>
                    <button className={styles.btnDanger} onClick={() => handleUnassign(product)}>
                      {t('dropshipping.unassignProduct')}
                    </button>
                  </>
                ) : (
                  <button className={styles.btnPrimary} onClick={() => openAssignModal(product)}>
                    {t('dropshipping.assignProduct')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('dropshipping.products')}</h1>
      </div>

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === 'products' ? styles.tabActive : ''}`} onClick={() => setActiveTab('products')}>
          {t('dropshipping.productsTab')}
        </button>
        <button className={`${styles.tab} ${activeTab === 'merchants' ? styles.tabActive : ''}`} onClick={() => setActiveTab('merchants')}>
          {t('dropshipping.merchantsTab')}
        </button>
      </div>

      {activeTab === 'products' && (
        <div className={styles.productsPanel}>
          {catalogStatus === 'ok' && (
            <div className={styles.filterBar}>
              <div className={styles.searchWrapper}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={t('dropshipping.searchProducts')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && <button className={styles.clearSearch} onClick={() => setSearchQuery('')}><X size={14} /></button>}
              </div>
              {connectedMerchants.length > 0 && (
                <div className={styles.selectWrapper}>
                  <select className={styles.select} value={filterMerchantId || ''} onChange={(e) => setFilterMerchantId(e.target.value ? parseInt(e.target.value) : null)}>
                    <option value="">{t('dropshipping.filterByMerchant')}</option>
                    {connectedMerchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <ChevronDown size={14} className={styles.selectIcon} />
                </div>
              )}
              <div className={styles.selectWrapper}>
                <select className={styles.select} value={filterAssigned} onChange={(e) => setFilterAssigned(e.target.value)}>
                  <option value="all">{t('dropshipping.allProducts')}</option>
                  <option value="assigned">{t('dropshipping.filterAssigned')}</option>
                  <option value="unassigned">{t('dropshipping.filterUnassigned')}</option>
                </select>
                <ChevronDown size={14} className={styles.selectIcon} />
              </div>
            </div>
          )}
          {renderProductsContent()}
        </div>
      )}

      {activeTab === 'merchants' && (
        <div className={styles.merchantsPanel}>
          {merchants.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{t('dropshipping.noMerchantsFound')}</p>
              <small>{t('dropshipping.noMerchantsDescription')}</small>
            </div>
          ) : (
            <div className={styles.merchantsGrid}>
              {merchants.map((merchant) => (
                <div key={merchant.id} className={styles.merchantCard}>
                  <div className={styles.merchantCardHeader}>
                    {merchant.logo ? (
                      <img src={getImageUrl(merchant.logo)} alt={merchant.name} className={styles.merchantLogo} />
                    ) : (
                      <div className={styles.merchantLogoPlaceholder}>
                        {merchant.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className={styles.merchantHeaderInfo}>
                      <div className={styles.merchantName}>{merchant.name}</div>
                      {merchant.isConnected && (
                        <span className={`${styles.statusBadge} ${styles.status_approved}`}>
                          {t('dropshipping.connected')}
                        </span>
                      )}
                    </div>
                  </div>

                  {merchant.contactPhone && (
                    <div className={styles.merchantPhone}>{merchant.contactPhone}</div>
                  )}

                  <div className={styles.merchantCharges}>
                    <div className={styles.merchantChargeRow}>
                      <span className={styles.chargeLabel}>{t('dropshipping.serviceChargeLabel')}</span>
                      <span className={styles.chargeValue}>{formatCurrency(merchant.serviceCharge || 0)}</span>
                    </div>
                    <div className={styles.merchantChargeRow}>
                      <span className={styles.chargeLabel}>{t('dropshipping.returnPenaltyLabel')}</span>
                      <span className={styles.chargeValue}>{formatCurrency(merchant.returnPenalty || 0)}</span>
                    </div>
                  </div>

                  <div className={styles.merchantActions}>
                    <button className={styles.btnOutline} onClick={() => { setActiveTab('products'); setFilterMerchantId(merchant.id); }}>
                      {t('dropshipping.viewProducts')}
                    </button>
                    {merchant.isConnected ? null : !merchant.requestStatus ? (
                      <button className={styles.btnPrimary} onClick={() => openApplyModal(merchant)}>
                        {t('dropshipping.apply')}
                      </button>
                    ) : (
                      <span className={`${styles.statusBadge} ${styles[`status_${merchant.requestStatus}`]}`}>
                        {statusLabel(merchant.requestStatus)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <div className={styles.modalOverlay} onClick={() => setAssignModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('dropshipping.assignProductTitle')}</h3>
            <p>{assignModal.name}</p>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.setSellingPrice')}</label>
              {assignModal.minimumSellingPrice && (
                <small className={styles.fieldHelp}>
                  {t('dropshipping.sellingPriceRange')
                    .replace('{{min}}', formatCurrency(assignModal.minimumSellingPrice))
                    .replace('{{max}}', formatCurrency(assignModal.maximumSellingPrice))}
                </small>
              )}
              <input
                type="number"
                className={styles.input}
                value={assignPrice}
                onChange={(e) => setAssignPrice(e.target.value)}
                min={assignModal.minimumSellingPrice || 0}
                max={assignModal.maximumSellingPrice || undefined}
                step="0.01"
              />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnOutline} onClick={() => setAssignModal(null)}>{t('dropshipping.cancel')}</button>
              <button className={styles.btnPrimary} onClick={handleAssign}>{t('dropshipping.assign')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Update Price Modal */}
      {priceModal && (
        <div className={styles.modalOverlay} onClick={() => setPriceModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('dropshipping.updatePriceTitle')}</h3>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.setSellingPrice')}</label>
              <input type="number" className={styles.input} value={assignPrice} onChange={(e) => setAssignPrice(e.target.value)} step="0.01" />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnOutline} onClick={() => setPriceModal(null)}>{t('dropshipping.cancel')}</button>
              <button className={styles.btnPrimary} onClick={handleUpdatePrice}>{t('dropshipping.updatePrice')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Apply to Provider Modal */}
      {applyModal && (
        <div className={styles.modalOverlay} onClick={() => setApplyModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('dropshipping.applyToProvider')}: {applyModal.name}</h3>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.requestedShopName')}</label>
              <input
                type="text"
                className={`${styles.input} ${applyErrors.requestedShopName ? styles.inputError : ''}`}
                placeholder={t('dropshipping.requestedShopNamePlaceholder')}
                value={applyForm.requestedShopName}
                onChange={(e) => { setApplyForm((p) => ({ ...p, requestedShopName: e.target.value })); setApplyErrors((p) => ({ ...p, requestedShopName: undefined })); }}
              />
              {applyErrors.requestedShopName && <span className={styles.fieldError}>{applyErrors.requestedShopName}</span>}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.deliveryCharge')}</label>
              <small className={styles.fieldHelp}>{t('dropshipping.deliveryChargeHelp')}</small>
              <input
                type="number"
                className={`${styles.input} ${applyErrors.deliveryCharge ? styles.inputError : ''}`}
                placeholder="0"
                min="0"
                step="0.01"
                value={applyForm.deliveryCharge}
                onChange={(e) => { setApplyForm((p) => ({ ...p, deliveryCharge: e.target.value })); setApplyErrors((p) => ({ ...p, deliveryCharge: undefined })); }}
              />
              {applyErrors.deliveryCharge && <span className={styles.fieldError}>{applyErrors.deliveryCharge}</span>}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.requestedPaymentCycle')}</label>
              <div className={styles.selectWrapper}>
                <select
                  className={styles.select}
                  value={applyForm.requestedPaymentCycle}
                  onChange={(e) => setApplyForm((p) => ({ ...p, requestedPaymentCycle: parseInt(e.target.value) }))}
                >
                  {(applyModal.allowedPaymentCycles || [7]).map((c) => (
                    <option key={c} value={c}>{c} {t('dropshipping.days')}</option>
                  ))}
                </select>
                <ChevronDown size={14} className={styles.selectIcon} />
              </div>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.resellerAccountName')}</label>
              <input
                type="text"
                className={`${styles.input} ${applyErrors.resellerAccountName ? styles.inputError : ''}`}
                value={applyForm.resellerAccountName}
                onChange={(e) => { setApplyForm((p) => ({ ...p, resellerAccountName: e.target.value })); setApplyErrors((p) => ({ ...p, resellerAccountName: undefined })); }}
              />
              {applyErrors.resellerAccountName && <span className={styles.fieldError}>{applyErrors.resellerAccountName}</span>}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.resellerAccountNumber')}</label>
              <input
                type="text"
                className={`${styles.input} ${applyErrors.resellerAccountNumber ? styles.inputError : ''}`}
                value={applyForm.resellerAccountNumber}
                onChange={(e) => { setApplyForm((p) => ({ ...p, resellerAccountNumber: e.target.value })); setApplyErrors((p) => ({ ...p, resellerAccountNumber: undefined })); }}
              />
              {applyErrors.resellerAccountNumber && <span className={styles.fieldError}>{applyErrors.resellerAccountNumber}</span>}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.resellerBank')}</label>
              <div className={styles.selectWrapper}>
                <select
                  className={`${styles.select} ${applyErrors.resellerBankId ? styles.inputError : ''}`}
                  value={applyForm.resellerBankId || ''}
                  onChange={(e) => { setApplyForm((p) => ({ ...p, resellerBankId: e.target.value ? parseInt(e.target.value) : null, resellerBankBranchId: null })); setApplyErrors((p) => ({ ...p, resellerBankId: undefined })); }}
                >
                  <option value="">{t('dropshipping.selectBank')}</option>
                  {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <ChevronDown size={14} className={styles.selectIcon} />
              </div>
              {applyErrors.resellerBankId && <span className={styles.fieldError}>{applyErrors.resellerBankId}</span>}
            </div>
            {branches.length > 0 && (
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t('dropshipping.resellerBankBranch')}</label>
                <div className={styles.selectWrapper}>
                  <select
                    className={`${styles.select} ${applyErrors.resellerBankBranchId ? styles.inputError : ''}`}
                    value={applyForm.resellerBankBranchId || ''}
                    onChange={(e) => { setApplyForm((p) => ({ ...p, resellerBankBranchId: e.target.value ? parseInt(e.target.value) : null })); setApplyErrors((p) => ({ ...p, resellerBankBranchId: undefined })); }}
                  >
                    <option value="">{t('dropshipping.selectBranch')}</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                  </select>
                  <ChevronDown size={14} className={styles.selectIcon} />
                </div>
                {applyErrors.resellerBankBranchId && <span className={styles.fieldError}>{applyErrors.resellerBankBranchId}</span>}
              </div>
            )}
            <div className={styles.modalActions}>
              <button className={styles.btnOutline} onClick={() => setApplyModal(null)}>{t('dropshipping.cancel')}</button>
              <button className={styles.btnPrimary} onClick={handleApply}>{t('dropshipping.submitApplication')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropshippingProductsPage;
